import React, { useEffect, useState } from 'react';
import { Send, Users, Mail, Phone, Loader2, AlertCircle, CheckCircle2, Zap } from 'lucide-react';
import { AdminAPI } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import RichTextEditor from './RichTextEditor';
import SequencesTab from './SequencesTab';

const TABS = [
  { id: 'manual', label: 'Email manuel', icon: Mail },
  { id: 'newsletter', label: 'Newsletter', icon: Users },
  { id: 'sequences', label: 'Séquences auto', icon: Zap },
];

const NotificationsTab = () => {
  const { toast } = useToast();
  const [tab, setTab] = useState('manual');
  const [users, setUsers] = useState([]);
  const [recipientQuery, setRecipientQuery] = useState('');

  // manual form
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [alsoSms, setAlsoSms] = useState('');
  const [sending, setSending] = useState(false);

  // newsletter form
  const [nlSubject, setNlSubject] = useState('');
  const [nlBody, setNlBody] = useState('');
  const [audience, setAudience] = useState('buyers');
  const [city, setCity] = useState('');
  const [nlSending, setNlSending] = useState(false);
  const [nlResult, setNlResult] = useState(null);

  useEffect(() => {
    AdminAPI.users().then(setUsers).catch(() => {});
  }, []);

  const filteredUsers = recipientQuery
    ? users.filter(u =>
        (u.email || '').toLowerCase().includes(recipientQuery.toLowerCase()) ||
        (u.name || '').toLowerCase().includes(recipientQuery.toLowerCase()))
        .slice(0, 8)
    : [];

  const send = async () => {
    if (!to || !subject || !bodyHtml) {
      toast({ title: 'Champs manquants', description: 'Destinataire, sujet et message sont requis.' });
      return;
    }
    setSending(true);
    try {
      const res = await AdminAPI.sendNotification({ to, subject, body_html: bodyHtml, also_sms: alsoSms || null });
      if (res.email?.mocked) {
        toast({ title: '⚠️ Resend non configuré', description: 'Ajoutez RESEND_API_KEY au .env pour activer l\'envoi.' });
      } else if (res.email?.ok) {
        toast({ title: '✅ Email envoyé', description: `ID : ${res.email.id || '—'}` });
        setTo(''); setSubject(''); setBodyHtml(''); setAlsoSms('');
      } else {
        toast({ title: 'Erreur', description: res.email?.error || 'Envoi échoué' });
      }
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Envoi échoué' });
    } finally { setSending(false); }
  };

  const sendNewsletter = async () => {
    if (!nlSubject || !nlBody) {
      toast({ title: 'Champs manquants', description: 'Sujet et contenu requis.' });
      return;
    }
    const audienceSize = users.filter(u =>
      (audience === 'buyers' ? u.role === 'buyer' :
       audience === 'suppliers' ? u.role === 'supplier' : true) &&
      (!city || (u.city || '').toLowerCase() === city.toLowerCase())
    ).length;
    if (!window.confirm(`Envoyer cette newsletter à ${audienceSize} utilisateur(s) ?`)) return;
    setNlSending(true); setNlResult(null);
    try {
      const res = await AdminAPI.sendNewsletter({ subject: nlSubject, body_html: nlBody, audience, city: city || null });
      setNlResult(res);
      if (res.email_enabled) {
        toast({ title: '✅ Newsletter envoyée', description: `${res.sent} email(s) envoyé(s) sur ${res.audience_size}` });
      } else {
        toast({ title: '⚠️ Resend non configuré', description: 'Aucun email réel n\'a été envoyé. Configurez RESEND_API_KEY.' });
      }
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Envoi échoué' });
    } finally { setNlSending(false); }
  };

  return (
    <div data-testid="admin-notifications-tab">
      <div className="flex gap-2 mb-5 border-b border-[#E8E0D0]">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              data-testid={`notif-tab-${t.id}`}
              className={`px-4 py-2 -mb-px border-b-2 text-sm font-medium flex items-center gap-1.5 whitespace-nowrap ${tab === t.id ? 'border-[#1F4D3F] text-[#1F4D3F]' : 'border-transparent text-[#7A6F5C]'}`}>
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'sequences' && <SequencesTab />}

      {tab === 'manual' && (
        <div className="bg-white border border-[#E8E0D0] rounded-2xl p-5 space-y-4">
          <p className="text-sm text-[#7A6F5C]">
            Envoyez un email transactionnel à un utilisateur précis (client ou fournisseur). Optionnellement, doublez d'un SMS bref.
          </p>
          <div className="relative">
            <label className="text-sm text-[#2D2A26] mb-1.5 block">Destinataire (email)</label>
            <Input value={to} onChange={(e) => { setTo(e.target.value); setRecipientQuery(e.target.value); }}
              placeholder="exemple@comptoir-online.com" data-testid="notif-manual-to" />
            {filteredUsers.length > 0 && to !== filteredUsers[0]?.email && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-[#E8E0D0] rounded-lg shadow-lg max-h-56 overflow-y-auto">
                {filteredUsers.map(u => (
                  <button key={u.user_id} type="button" onClick={() => { setTo(u.email); setRecipientQuery(''); }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-[#FAF7F2] flex items-center justify-between">
                    <span><strong>{u.name || u.email}</strong> <span className="text-[#7A6F5C]">— {u.email}</span></span>
                    <span className="text-[10px] uppercase text-[#7A6F5C]">{u.role}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="text-sm text-[#2D2A26] mb-1.5 block">Sujet</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Sujet de l'email" data-testid="notif-manual-subject" />
          </div>
          <div>
            <label className="text-sm text-[#2D2A26] mb-1.5 block">Message</label>
            <RichTextEditor value={bodyHtml} onChange={setBodyHtml} placeholder="Tapez votre message…" testId="notif-manual-body" minHeight={200} />
          </div>
          <div>
            <label className="text-sm text-[#2D2A26] mb-1.5 block flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> Aussi par SMS (optionnel)</label>
            <Input value={alsoSms} onChange={(e) => setAlsoSms(e.target.value)} placeholder="+213 5XX XX XX XX" data-testid="notif-manual-sms" />
            <p className="text-xs text-[#7A6F5C] mt-1">Si renseigné, un SMS court sera envoyé en plus de l'email (Twilio requis).</p>
          </div>
          <div className="flex justify-end">
            <Button onClick={send} disabled={sending} className="bg-[#1F4D3F] hover:bg-[#173B30] text-white" data-testid="notif-manual-send">
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Envoyer
            </Button>
          </div>
        </div>
      )}

      {tab === 'newsletter' && (
        <div className="bg-white border border-[#E8E0D0] rounded-2xl p-5 space-y-4">
          <p className="text-sm text-[#7A6F5C]">
            Envoyez une campagne à tous les utilisateurs d'un segment. Utilisez le filtre <em>ville</em> pour cibler une zone géographique.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-[#2D2A26] mb-1.5 block">Audience</label>
              <select value={audience} onChange={(e) => setAudience(e.target.value)} data-testid="newsletter-audience"
                className="w-full h-10 rounded-lg border border-[#E8E0D0] bg-white px-3 text-sm">
                <option value="buyers">Acheteurs (dentistes)</option>
                <option value="suppliers">Fournisseurs</option>
                <option value="all">Tout le monde</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-[#2D2A26] mb-1.5 block">Ville (optionnel)</label>
              <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Alger, Oran, Constantine…" data-testid="newsletter-city" />
            </div>
          </div>
          <div>
            <label className="text-sm text-[#2D2A26] mb-1.5 block">Sujet de l'email</label>
            <Input value={nlSubject} onChange={(e) => setNlSubject(e.target.value)} placeholder="Ex : Soldes d'hiver — jusqu'à -30%" data-testid="newsletter-subject" />
          </div>
          <div>
            <label className="text-sm text-[#2D2A26] mb-1.5 block">Contenu</label>
            <RichTextEditor value={nlBody} onChange={setNlBody} placeholder="Rédigez votre campagne…" testId="newsletter-body" minHeight={260} />
          </div>
          <div className="flex items-center justify-between gap-3 pt-2 border-t border-[#F5F1EA]">
            <div className="text-xs text-[#7A6F5C]">
              <Users className="w-3.5 h-3.5 inline mr-1" />
              Audience estimée : <strong className="text-[#1F4D3F]">{users.filter(u =>
                (audience === 'buyers' ? u.role === 'buyer' :
                 audience === 'suppliers' ? u.role === 'supplier' : true) &&
                (!city || (u.city || '').toLowerCase() === city.toLowerCase())
              ).length}</strong> utilisateur(s)
            </div>
            <Button onClick={sendNewsletter} disabled={nlSending} className="bg-[#1F4D3F] hover:bg-[#173B30] text-white" data-testid="newsletter-send">
              {nlSending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Envoyer la newsletter
            </Button>
          </div>
          {nlResult && (
            <div className={`rounded-lg p-3 text-sm flex items-start gap-2 ${nlResult.email_enabled ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800'}`} data-testid="newsletter-result">
              {nlResult.email_enabled ? <CheckCircle2 className="w-4 h-4 mt-0.5" /> : <AlertCircle className="w-4 h-4 mt-0.5" />}
              <div>
                <div className="font-medium">{nlResult.email_enabled ? 'Campagne envoyée' : 'Mode simulé — Resend non configuré'}</div>
                <div className="text-xs mt-1">
                  {nlResult.sent} envoyé(s) · {nlResult.failed} échec(s) · {nlResult.skipped_no_email} sans email · audience {nlResult.audience_size}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="mt-4 bg-[#FAF7F2] border border-[#E8E0D0] rounded-lg p-3 text-xs text-[#7A6F5C]">
        <strong className="text-[#1F4D3F]">💡 Configuration :</strong> ajoutez <code className="px-1 bg-white rounded">RESEND_API_KEY</code> dans <code className="px-1 bg-white rounded">/app/backend/.env</code> pour activer l'envoi réel. Tant qu'aucune clé n'est définie, les emails sont simulés (loggés mais non envoyés).
      </div>
    </div>
  );
};

export default NotificationsTab;
